import {makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import {ModalType} from "data/enums";
import type {IModalPayload, IModalsStore} from "data/stores/modals/modals.store";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IPlayersStore} from "data/stores/players/players.store";
import {useNavigate} from "react-router-dom";
import {AnalyticService} from "data/utils/track_analytics";

interface IProps {
	gameFeedId: number;
	navigate: ReturnType<typeof useNavigate>;
}

export interface IModalConfirmController extends ViewController<IProps> {
	i18n: ILocalizationStore;

	get isOpen(): boolean;
	get modalContent(): IModalPayload | null;
	get playerFullName(): string | undefined;

	close: () => void;
	toLeaderboard: () => void;
	toLanding: () => void;
}

@injectable()
export class ModalConfirmController implements IModalConfirmController {
	@observable private _navigate?: ReturnType<typeof useNavigate>;
	@observable _gameFeedId: number = 0;
	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.ModalsStore) private readonly _modalsStore: IModalsStore,
		@inject(Bindings.PlayersStore) private _playerStore: IPlayersStore
	) {
		makeAutoObservable(this);
	}

	get isOpen(): boolean {
		return this._modalsStore.modal === ModalType.CONFIRM;
	}

	get modalContent() {
		return this._modalsStore.modalContent;
	}

	get playerFullName() {
		const playerId = this._modalsStore.modalContent?.playerId ?? 0;
		if (!playerId) return;

		const player = this._playerStore.getPlayerById(playerId);
		return `${String(player?.firstName)} ${String(player?.lastName)}`;
	}

	close = () => {
		this._modalsStore.hideModal();
		localStorage.removeItem("localPrediction");

		AnalyticService.trackAction({
			"action.name": "Player of the Game: Vote Submitted Modal: Close Click",
			"action.location": "Major League Baseball: Player of the Game: Vote Submitted Modal",
			"action.category": "Player of the Game",
			"action.element.text": "Close Submitted modal",
			"baseball.gamePk": String(this._gameFeedId),
		});
	};

	toLeaderboard = () => {
		this.close();
		this._navigate?.("/leaderboard");
	};

	toLanding = () => {
		this.close();
		this._navigate?.("/");
	};

	init({gameFeedId, navigate}: IProps) {
		this._gameFeedId = gameFeedId;
		this._navigate = navigate;
	}
}
